import React, { useState } from 'react';
import { Nav } from '../components/Nav.tsx';
import { Footer } from '../components/Footer.tsx';

// ───────────────────────────────────────────────────────────────────────────
// HubSpot connection
// Replace the two placeholders below with your real IDs, then re-publish.
//   • HUBSPOT_PORTAL_ID — your HubSpot account / hub ID (digits, e.g. "1234567")
//   • HUBSPOT_FORM_ID   — the form GUID (e.g. "a1b2c3d4-1111-2222-3333-abcdef123456")
//   • HUBSPOT_REGION    — "na1" (default US), "na2", or "eu1" — match your account
// The form names below (firstname, lastname, email, company, jobtitle, phone,
// message) are HubSpot's default contact properties. Make sure these fields
// exist on the HubSpot form you point to.
// ───────────────────────────────────────────────────────────────────────────
const HUBSPOT_PORTAL_ID = '2687105';
const HUBSPOT_FORM_ID = '548706c3-d876-4b77-9ecb-bff62b9a557c';
const HUBSPOT_REGION: string = 'na1';
const HUBSPOT_CONFIGURED = HUBSPOT_PORTAL_ID.length > 0 && HUBSPOT_FORM_ID.length > 0;
function getCookie(name: string) {
  const m = document.cookie.match(new RegExp('(^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[2]) : undefined;
}
const INK = '#091717';
const CREAM = '#FCFAF6';
type Status = 'idle' | 'submitting' | 'success' | 'error';
const initialForm = {
  firstname: '',
  lastname: '',
  email: '',
  company: '',
  website: '',
  jobtitle: '',
  phone: '',
  message: ''
};
const VALUE_PROPS = ['A personalized walkthrough of AI Storefront and MCP Apps', 'See how your catalog becomes readable for AI agents', 'Get straight answers on pricing, onboarding, and timelines'];
const LOGO_BASE = 'https://maker-devi-dev.s3-accelerate.amazonaws.com/user-files/org_2REc1tnPEsPISxOixJ2l0CV1dpe/';
const LOGOS = [{
  url: LOGO_BASE + 'logo-1-7uox6tm8dl3.png',
  alt: 'Brand 1'
}, {
  url: LOGO_BASE + 'logo-2-tz2mhq4dv1.png',
  alt: 'Brand 2'
}, {
  url: LOGO_BASE + 'logo-3-lh6i2tsuqjr.png',
  alt: 'Brand 3'
}, {
  url: LOGO_BASE + 'logo-4-qh95xz1yxuc.png',
  alt: 'Brand 4'
}, {
  url: LOGO_BASE + 'logo-5-fe93nfq7rdn.png',
  alt: 'Brand 5'
}, {
  url: LOGO_BASE + 'logo-6-bnjr6o8mjvl.png',
  alt: 'Brand 6'
}, {
  url: LOGO_BASE + 'logo-7-y8t5opowp.png',
  alt: 'Brand 7'
}, {
  url: LOGO_BASE + 'logo-8-kvdzezq2byc.png',
  alt: 'Brand 8'
}, {
  url: LOGO_BASE + 'logo-9-4e8beemjuv3.png',
  alt: 'Brand 9'
}];
const Check = () => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true" style={{
  flexShrink: 0,
  marginTop: '2px'
}}>
    <circle cx="10" cy="10" r="10" fill={INK} />
    <path d="M5.8 10.2l2.6 2.6 5.8-5.8" stroke={CREAM} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
const FIELD_INPUT_STYLE: React.CSSProperties = {
  width: '100%',
  padding: '12px 14px',
  border: '1px solid rgba(9,23,23,0.18)',
  borderRadius: '10px',
  fontFamily: 'Instrument Sans, sans-serif',
  fontSize: '16px',
  color: INK,
  background: '#FFFFFF',
  outline: 'none',
  transition: 'border-color 0.2s ease'
};
const FIELD_LABEL_STYLE: React.CSSProperties = {
  display: 'block',
  fontFamily: 'Instrument Sans, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  color: INK,
  marginBottom: '6px'
};
const fieldFocus = (e: React.FocusEvent<HTMLInputElement>) => {
  e.currentTarget.style.borderColor = INK;
};
const fieldBlur = (e: React.FocusEvent<HTMLInputElement>) => {
  e.currentTarget.style.borderColor = 'rgba(9,23,23,0.18)';
};
const Field = ({
  id,
  label,
  type = 'text',
  required = false,
  autoComplete,
  value,
  onChange
}: {
  id: string;
  label: string;
  type?: string;
  required?: boolean;
  autoComplete?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) => <div>
    <label htmlFor={id} style={FIELD_LABEL_STYLE}>
      {label}{required && <span style={{
      color: 'rgba(9,23,23,0.5)'
    }}> *</span>}
    </label>
    <input id={id} name={id} type={type} required={required} autoComplete={autoComplete} value={value} onChange={onChange} onFocus={fieldFocus} onBlur={fieldBlur} style={FIELD_INPUT_STYLE} />
  </div>;
export const BookDemo = () => {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState('');
  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm(f => ({
    ...f,
    [key]: e.target.value
  }));
  const onFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    e.currentTarget.style.borderColor = INK;
  };
  const onBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    e.currentTarget.style.borderColor = 'rgba(9,23,23,0.18)';
  };
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setStatus('submitting');
    const fields = Object.entries(form).filter(([, value]) => value.trim() !== '').map(([name, value]) => ({
      name,
      value: value.trim()
    }));
    // Not yet wired to a real HubSpot form — show the confirmation state so the
    // page is fully demoable, and leave a console note for whoever configures it.
    if (!HUBSPOT_CONFIGURED) {
      console.warn('[Book a demo] HubSpot is not configured yet. Set HUBSPOT_PORTAL_ID and HUBSPOT_FORM_ID in src/pages/BookDemo.tsx to start capturing real submissions.');
      setTimeout(() => setStatus('success'), 600);
      return;
    }
    let ipAddress: string | undefined;
    try {
      ipAddress = await fetch('https://api.ipify.org?format=json').then(r => r.json()).then(d => d.ip);
    } catch {
      /* IP is best-effort; HubSpot still accepts the submission without it */
    }
    try {
      const base = HUBSPOT_REGION === 'eu1' ? 'https://api-eu1.hsforms.com' : 'https://api.hsforms.com';
      const res = await fetch(`${base}/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${HUBSPOT_FORM_ID}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fields,
          context: {
            hutk: getCookie('hubspotutk'),
            ipAddress,
            pageUri: window.location.href,
            pageName: document.title
          }
        })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.message || `Submission failed (${res.status}). Please try again.`);
      }
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  };
  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 14px',
    border: '1px solid rgba(9,23,23,0.18)',
    borderRadius: '10px',
    fontFamily: 'Instrument Sans, sans-serif',
    fontSize: '16px',
    color: INK,
    background: '#FFFFFF',
    outline: 'none',
    transition: 'border-color 0.2s ease'
  };
  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontFamily: 'Instrument Sans, sans-serif',
    fontSize: '14px',
    fontWeight: 500,
    color: INK,
    marginBottom: '6px'
  };
  return <div className="bg-cream" style={{
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column'
  }}>
      <style>{`
        .bd-input::placeholder { color: rgba(9,23,23,0.4); }
        @media (max-width: 1024px) {
          .bd-grid { grid-template-columns: 1fr !important; gap: 40px !important; padding-top: 120px !important; padding-bottom: 60px !important; }
          .bd-heading { font-size: clamp(48px, 9vw, 76px) !important; }
        }
        @media (max-width: 640px) {
          .bd-name-row { grid-template-columns: 1fr !important; }
          .bd-card { padding: 24px !important; }
        }
      `}</style>
      <Nav />
      <main id="main-content" tabIndex={-1} style={{
      flex: 1
    }}>
        <section data-name="Book Demo" data-nav-theme="light">
          <div className="bd-grid max-w-8xl" style={{
          width: '100%',
          margin: '0 auto',
          paddingLeft: '24px',
          paddingRight: '24px',
          paddingTop: '160px',
          paddingBottom: '100px',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '64px',
          alignItems: 'start'
        }}>
            {/* Left — copy */}
            <div data-name="Demo Copy" className="animate-fade-slide-up">
              <h1 className="bd-heading" style={{
              color: INK,
              lineHeight: 1.02,
              letterSpacing: '-0.04em',
              fontSize: 'clamp(60px, 7vw, 104px)',
              margin: 0
            }}>
                <span className="font-sans font-normal">Book a</span>{' '}
                <span className="font-serif italic" style={{
                letterSpacing: '-0.02em'
              }}>demo</span>
              </h1>
              <p className="font-sans" style={{
              color: 'rgba(9,23,23,0.75)',
              fontSize: 'clamp(16px, 1.4vw, 20px)',
              lineHeight: 1.5,
              maxWidth: '46ch',
              marginTop: '24px'
            }}>
                Tell us a little about your store and we’ll show you how GreatStore makes it
                beautiful for shoppers and readable for the AI agents buying on their behalf.
              </p>
              <ul style={{
              listStyle: 'none',
              padding: 0,
              margin: '40px 0 0',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
                {VALUE_PROPS.map(text => <li key={text} style={{
                display: 'flex',
                gap: '12px',
                alignItems: 'flex-start'
              }}>
                    <Check />
                    <span className="font-sans" style={{
                  color: INK,
                  fontSize: '16px',
                  lineHeight: 1.45
                }}>{text}</span>
                  </li>)}
              </ul>
            </div>

            {/* Right — form card */}
            <div className="bd-card animate-fade-slide-up-delay-1" data-name="Demo Form" style={{
            background: '#FFFCF7',
            border: '1px solid rgba(9,23,23,0.08)',
            borderRadius: '20px',
            padding: '40px'
          }}>
              {status === 'success' ? <div style={{
              textAlign: 'center',
              padding: '24px 8px'
            }}>
                  <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '999px',
                background: INK,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                    <svg width="28" height="28" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                      <path d="M5 10.4l3 3 7-7.4" stroke={CREAM} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <h2 className="font-sans" style={{
                color: INK,
                fontSize: '28px',
                letterSpacing: '-0.02em',
                margin: 0
              }}>Thank you for your submission</h2>
                  <p className="font-sans" style={{
                color: 'rgba(9,23,23,0.7)',
                fontSize: '16px',
                lineHeight: 1.5,
                marginTop: '12px'
              }}>
                    Our team will reach out shortly to schedule your personalized GreatStore demo.
                  </p>
                  <a href="/" className="font-sans font-medium" style={{
                display: 'inline-block',
                marginTop: '28px',
                background: INK,
                color: CREAM,
                borderRadius: '999px',
                padding: '12px 24px',
                textDecoration: 'none'
              }} data-tracking-id="link-bookdemo-success-home">Back to home</a>
                </div> : <form onSubmit={submit} noValidate style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '18px'
            }}>
                  <div className="bd-name-row" style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '18px'
              }}>
                    <Field id="firstname" label="First name" required autoComplete="given-name" value={form.firstname} onChange={set('firstname')} />
                    <Field id="lastname" label="Last name" required autoComplete="family-name" value={form.lastname} onChange={set('lastname')} />
                  </div>
                  <Field id="email" label="Work email" type="email" required autoComplete="email" value={form.email} onChange={set('email')} />
                  <Field id="company" label="Company" required autoComplete="organization" value={form.company} onChange={set('company')} />
                  <Field id="website" label="Store URL" type="url" required autoComplete="url" value={form.website} onChange={set('website')} />
                  <div className="bd-name-row" style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '18px'
              }}>
                    <Field id="jobtitle" label="Job title" autoComplete="organization-title" value={form.jobtitle} onChange={set('jobtitle')} />
                    <Field id="phone" label="Phone" type="tel" autoComplete="tel" value={form.phone} onChange={set('phone')} />
                  </div>
                  <div>
                    <label htmlFor="message" style={labelStyle}>What would you like to see?</label>
                    <textarea id="message" name="message" rows={4} value={form.message} onChange={set('message')} onFocus={onFocus} onBlur={onBlur} className="bd-input" style={{
                  ...inputStyle,
                  resize: 'vertical',
                  minHeight: '96px'
                }} />
                  </div>

                  {status === 'error' && <p role="alert" className="font-sans" style={{
                color: '#B42318',
                fontSize: '14px',
                margin: 0
              }}>{error}</p>}

                  <button type="submit" disabled={status === 'submitting'} className="font-sans font-medium" style={{
                marginTop: '6px',
                width: '100%',
                background: INK,
                color: CREAM,
                border: 'none',
                borderRadius: '999px',
                padding: '14px 24px',
                fontSize: '16px',
                cursor: status === 'submitting' ? 'default' : 'pointer',
                opacity: status === 'submitting' ? 0.7 : 1,
                transition: 'opacity 0.2s ease'
              }} onMouseEnter={e => {
                if (status !== 'submitting') (e.currentTarget as HTMLButtonElement).style.opacity = '0.85';
              }} onMouseLeave={e => {
                if (status !== 'submitting') (e.currentTarget as HTMLButtonElement).style.opacity = '1';
              }} data-tracking-id="btn-bookdemo-submit">
                    {status === 'submitting' ? 'Sending…' : 'Book a demo'}
                  </button>

                  <p className="font-sans" style={{
                color: 'rgba(9,23,23,0.5)',
                fontSize: '13px',
                lineHeight: 1.5,
                margin: 0,
                textAlign: 'center'
              }}>
                    By submitting, you agree to be contacted about GreatStore. We’ll never share your details.
                  </p>
                </form>}
            </div>
          </div>
        </section>

        {/* Trusted-by marquee — mirrors the homepage for brand continuity + social proof */}
        <section data-name="Trusted Bar" aria-label="Trusted brands" className="animate-fade-slide-up-delay-2" style={{
        paddingBottom: '72px'
      }}>
          <p className="font-sans" style={{
          textAlign: 'center',
          color: 'rgba(9,23,23,0.7)',
          fontSize: 'clamp(15px, 1.1vw, 18px)',
          fontWeight: 500,
          marginBottom: '24px'
        }}>
            Trusted by brands that care about growth
          </p>
          <div className="overflow-hidden w-full marquee-overflow">
            <div className="marquee-track">
              {LOGOS.map((logo, i) => <img key={`a-${i}`} src={logo.url} alt={logo.alt} className="h-[28px] md:h-[36px] lg:h-[44px] w-auto object-contain flex-shrink-0 mx-5 md:mx-10" style={{
              filter: 'brightness(0)',
              opacity: 0.5
            }} />)}
              {LOGOS.map((logo, i) => <img key={`b-${i}`} src={logo.url} alt="" aria-hidden="true" className="h-[28px] md:h-[36px] lg:h-[44px] w-auto object-contain flex-shrink-0 mx-5 md:mx-10" style={{
              filter: 'brightness(0)',
              opacity: 0.5
            }} />)}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>;
};
export default BookDemo;